/**
 * useRewards Hook — Sprint 17
 * Provides rewards data (points, tier, transactions) for UI.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useEffect } from 'react';
import { NS } from '@ownyou/shared-types';
import { useStore } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';

export type RewardTier = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
export type RewardCategory = 'explorer' | 'connector' | 'helper' | 'achiever';

export interface RewardTransaction {
  id: string;
  type: 'mission_completion' | 'refinement' | 'threshold_crossed' | 'data_shared';
  missionId?: string;
  missionTitle?: string;
  points: number;
  category: RewardCategory;
  timestamp: number;
  threshold?: number;
  description?: string;
}

export function calculateTier(totalPoints: number): RewardTier {
  if (totalPoints >= 10000) return 'diamond';
  if (totalPoints >= 2000) return 'platinum';
  if (totalPoints >= 500) return 'gold';
  if (totalPoints >= 100) return 'silver';
  return 'bronze';
}

export function getNextTierInfo(tier: RewardTier): { tier: RewardTier; pointsNeeded: number } | null {
  const thresholds: Record<RewardTier, number> = {
    bronze: 0, silver: 100, gold: 500, platinum: 2000, diamond: 10000,
  };
  const order: RewardTier[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];
  const idx = order.indexOf(tier);
  if (idx >= order.length - 1) return null;
  const next = order[idx + 1];
  return { tier: next, pointsNeeded: thresholds[next] };
}

interface UseRewardsOptions {
  requireAuthenticatedWallet?: boolean;
}

function emptyRewards() {
  return {
    totalPoints: 0,
    tier: 'bronze' as RewardTier,
    transactions: [] as RewardTransaction[],
    categoryBreakdown: {} as Record<string, number>,
  };
}

export function useRewards(options: UseRewardsOptions = {}) {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const userId = wallet?.address ?? (options.requireAuthenticatedWallet ? null : 'anonymous');
  const enabled = isReady && Boolean(userId);

  const query = useQuery({
    queryKey: ['rewards', userId ?? 'no-wallet', isReady],
    queryFn: async () => {
      if (!store || !isReady || !userId) {
        return emptyRewards();
      }

      const rewardsNs = NS.rewardsTotals(userId);
      const transactionsNs = NS.rewardsTransactions(userId);

      // Get total points
      let totalPoints = 0;
      try {
        const total = await store.get<{ value: number }>(rewardsNs, 'total');
        totalPoints = total?.value ?? 0;
      } catch {
        // No rewards yet
      }

      // Get category breakdown
      const categoryBreakdown: Record<string, number> = {};
      for (const cat of ['explorer', 'connector', 'helper', 'achiever']) {
        try {
          const catData = await store.get<{ value: number }>(rewardsNs, `category:${cat}`);
          categoryBreakdown[cat] = catData?.value ?? 0;
        } catch {
          categoryBreakdown[cat] = 0;
        }
      }

      // Get recent transactions
      let transactions: RewardTransaction[] = [];
      try {
        const result = await store.list<RewardTransaction>(transactionsNs, { limit: 20, offset: 0 });
        transactions = result.items ?? [];
      } catch {
        // No transactions yet
      }

      const tier = calculateTier(totalPoints);

      return { totalPoints, tier, transactions, categoryBreakdown };
    },
    enabled,
    staleTime: 1000 * 60 * 2,
  });

  // Sprint 17g-4: Re-read totals on focus for cross-page reactivity
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  useEffect(() => {
    const handleFocus = () => refetchRef.current();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return {
    totalPoints: query.data?.totalPoints ?? 0,
    tier: query.data?.tier ?? ('bronze' as RewardTier),
    transactions: query.data?.transactions ?? [],
    categoryBreakdown: query.data?.categoryBreakdown ?? {},
    isLoading: query.isLoading,
  };
}

// Sprint 17d Package 8: Point values by mission type
// Includes both agent types (restaurant, events) and UI types (food, entertainment)
// so lookups work regardless of whether the caller maps types first.
const MISSION_POINTS: Record<string, { points: number; category: RewardCategory }> = {
  shopping: { points: 10, category: 'explorer' },
  food: { points: 15, category: 'explorer' },
  restaurant: { points: 15, category: 'explorer' },
  content: { points: 5, category: 'explorer' },
  travel: { points: 25, category: 'explorer' },
  entertainment: { points: 15, category: 'explorer' },
  events: { points: 15, category: 'explorer' },
  savings: { points: 20, category: 'achiever' },
  health: { points: 15, category: 'helper' },
  people: { points: 10, category: 'connector' },
  consumables: { points: 10, category: 'explorer' },
};

const DEFAULT_REWARD: { points: number; category: RewardCategory } = { points: 5, category: 'explorer' };

// Sprint 17g-4: Extra award types (refinement, data sharing, thresholds)
const EXTRA_AWARDS: Record<string, { points: number; category: RewardCategory }> = {
  refinement: { points: 5, category: 'connector' },
  data_shared: { points: 15, category: 'connector' },
};

/** Look up reward config for a mission type (agent or UI type). Exported for testing. */
export function getRewardConfig(missionType: string): { points: number; category: RewardCategory } {
  return MISSION_POINTS[missionType] ?? DEFAULT_REWARD;
}

/**
 * Hook to award points when a mission is completed.
 * Writes a RewardTransaction and updates totals in the store.
 */
export function useAwardPoints() {
  const { store, isReady } = useStore();
  const { wallet } = useAuth();
  const queryClient = useQueryClient();
  const awardingRef = useRef(false);

  const userId = wallet?.address ?? 'anonymous';

  const awardPoints = useCallback(async (
    missionId: string,
    missionTitle: string,
    missionType: string,
  ) => {
    if (!store || !isReady || !wallet) return;
    if (awardingRef.current) return;
    awardingRef.current = true;

    const rewardsNs = NS.rewardsTotals(userId);
    const transactionsNs = NS.rewardsTransactions(userId);

    try {
      const config = getRewardConfig(missionType);
      const now = Date.now();

      // 1. Write transaction
      const transaction: RewardTransaction = {
        id: `reward_${now}_${Math.random().toString(36).slice(2, 6)}`,
        type: 'mission_completion',
        missionId,
        missionTitle,
        points: config.points,
        category: config.category,
        timestamp: now,
      };

      await store.put(transactionsNs, transaction.id, transaction);

      // 2. Update total points
      let currentTotal = 0;
      try {
        const existing = await store.get<{ value: number }>(rewardsNs, 'total');
        currentTotal = existing?.value ?? 0;
      } catch { /* first reward */ }
      await store.put(rewardsNs, 'total', { value: currentTotal + config.points });

      // 3. Update category total
      let currentCat = 0;
      try {
        const existing = await store.get<{ value: number }>(rewardsNs, `category:${config.category}`);
        currentCat = existing?.value ?? 0;
      } catch { /* first in category */ }
      await store.put(rewardsNs, `category:${config.category}`, { value: currentCat + config.points });

      // 4. Invalidate rewards query to refresh UI
      queryClient.invalidateQueries({ queryKey: ['rewards'] });

      return transaction;
    } finally {
      awardingRef.current = false;
    }
  }, [store, isReady, wallet, queryClient, userId]);

  // Sprint 17g-4: Award bonus points for non-mission activities
  const awardBonus = useCallback(async (
    type: 'refinement' | 'threshold_crossed' | 'data_shared',
    description: string,
    points?: number,
    meta?: { threshold?: number; category?: string },
  ) => {
    if (!store || !isReady || !wallet) return;

    const rewardsNs = NS.rewardsTotals(userId);
    const transactionsNs = NS.rewardsTransactions(userId);

    const config = EXTRA_AWARDS[type] ?? { points: points ?? 0, category: 'explorer' as RewardCategory };
    const awarded = points ?? config.points;

    const transaction: RewardTransaction = {
      id: `reward_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      type,
      points: awarded,
      category: config.category,
      timestamp: Date.now(),
      description,
      threshold: meta?.threshold,
    };

    try {
      await store.put(transactionsNs, transaction.id, transaction);

      let currentTotal = 0;
      try {
        const existing = await store.get<{ value: number }>(rewardsNs, 'total');
        currentTotal = existing?.value ?? 0;
      } catch { /* first reward */ }
      await store.put(rewardsNs, 'total', { value: currentTotal + awarded });

      let currentCat = 0;
      try {
        const existing = await store.get<{ value: number }>(rewardsNs, `category:${config.category}`);
        currentCat = existing?.value ?? 0;
      } catch { /* first in category */ }
      await store.put(rewardsNs, `category:${config.category}`, { value: currentCat + awarded });

      queryClient.invalidateQueries({ queryKey: ['rewards'] });
      return transaction;
    } catch {
      // Best effort
    }
  }, [store, isReady, wallet, queryClient, userId]);

  return { awardPoints, awardBonus };
}
